import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Home,
  Paintbrush,
  HardHat,
  Zap,
  ClipboardList,
  Hammer,
  Wrench,
  CheckCircle,
  Calendar,
  Phone,
} from "lucide-react";

const services = [
  {
    title: "General Contracting",
    description:
      "As your general contractor, we oversee every aspect of your construction project from start to finish. We coordinate all trades, manage schedules, ensure quality control, and maintain clear communication throughout the process. Our comprehensive approach ensures your project is completed on time, within budget, and to the highest standards.",
    icon: HardHat,
    features: [
      "Complete project oversight",
      "Trade coordination",
      "Schedule management",
      "Quality assurance",
      "Budget control",
      "Permit acquisition",
    ],
  },
  {
    title: "Commercial Construction",
    description:
      "We specialize in commercial construction projects of all sizes, from retail spaces and restaurants to office buildings and medical facilities. Our team understands the unique requirements of commercial projects, including compliance with commercial building codes, ADA accessibility, and the need for minimal disruption to surrounding businesses.",
    icon: Building2,
    features: [
      "Office buildings",
      "Retail spaces",
      "Medical facilities",
      "Restaurants & hospitality",
      "Industrial facilities",
      "Warehouse construction",
    ],
  },
  {
    title: "Residential Renovations",
    description:
      "Transform your home with our comprehensive residential renovation services. Whether you're updating a single room or undertaking a whole-house renovation, our team brings expertise, creativity, and attention to detail to every project. We work closely with homeowners to understand their vision and deliver results that exceed expectations.",
    icon: Home,
    features: [
      "Kitchen remodeling",
      "Bathroom renovations",
      "Basement finishing",
      "Room additions",
      "Whole-house renovations",
      "Historic restoration",
    ],
  },
  {
    title: "Interior Build-Outs",
    description:
      "Our interior build-out services transform raw commercial spaces into functional, attractive environments. From initial design consultation through final installation, we handle every aspect of your interior construction project. We specialize in creating spaces that reflect your brand identity while maximizing functionality.",
    icon: Paintbrush,
    features: [
      "Space planning",
      "Partition walls",
      "Ceiling systems",
      "Flooring installation",
      "Custom millwork",
      "Fixture installation",
    ],
  },
  {
    title: "Electrical Contracting Coordination",
    description:
      "We coordinate all electrical work for your construction project, ensuring safe, code-compliant installations that meet your power needs. Our network of licensed electricians handles everything from basic wiring to complex commercial electrical systems, including backup power solutions and energy-efficient lighting.",
    icon: Zap,
    features: [
      "Electrical system design",
      "Wiring & rewiring",
      "Panel upgrades",
      "Lighting installation",
      "Emergency systems",
      "Energy efficiency upgrades",
    ],
  },
  {
    title: "Project Management",
    description:
      "Our professional project management services ensure your construction project runs smoothly from concept to completion. We provide dedicated project managers who oversee every detail, coordinate between all parties, manage timelines and budgets, and keep you informed every step of the way.",
    icon: ClipboardList,
    features: [
      "Timeline development",
      "Budget management",
      "Vendor coordination",
      "Progress reporting",
      "Risk management",
      "Quality control",
    ],
  },
  {
    title: "Structural & Architectural Improvements",
    description:
      "From structural repairs to architectural enhancements, we have the expertise to handle complex construction challenges. Our team works with structural engineers and architects to ensure your project is safe, code-compliant, and aesthetically pleasing.",
    icon: Hammer,
    features: [
      "Structural repairs",
      "Foundation work",
      "Load-bearing modifications",
      "Exterior renovations",
      "Facade improvements",
      "Seismic retrofitting",
    ],
  },
  {
    title: "Custom Construction Projects",
    description:
      "Have a unique construction need? Our custom construction services can bring your vision to life. We've tackled everything from custom home features to specialized commercial installations. If you can dream it, we can build it.",
    icon: Wrench,
    features: [
      "Custom home features",
      "Specialty installations",
      "Unique design elements",
      "Problem-solving",
      "Creative solutions",
      "One-of-a-kind projects",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-32 bg-[#0A0A0A] overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1920"
            alt="Construction Services"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
            Our Services
          </span>
          <h1 className="font-['Oswald'] text-5xl md:text-6xl font-bold text-white mb-6">
            Comprehensive<br />Construction Solutions
          </h1>
          <div className="section-divider" />
          <p className="text-[#A1A1AA] text-xl max-w-2xl mt-6">
            From general contracting to specialized build-outs, we deliver expert
            construction services for residential and commercial projects.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 bg-[#121212]" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:grid-flow-dense" : ""
                }`}
                data-testid={`service-section-${index}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? "lg:col-start-2" : ""}>
                  <div className="text-[#7ED4C6] mb-4">
                    <service.icon size={48} strokeWidth={1.5} />
                  </div>
                  <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
                    {service.title}
                  </h2>
                  <div className="section-divider" />
                  <p className="text-[#A1A1AA] leading-relaxed mt-6 mb-8">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-white text-sm">
                        <CheckCircle size={16} className="text-[#7ED4C6] flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid={`service-cta-${index}`}
                  >
                    Get a Quote
                    <ArrowRight size={18} />
                  </Link>
                </div>

                {/* Visual */}
                <div
                  className={`relative ${index % 2 === 1 ? "lg:col-start-1" : ""}`}
                >
                  <div className="aspect-[4/3] bg-[#1E1E1E] flex items-center justify-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#7ED4C6]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <service.icon
                      size={120}
                      strokeWidth={0.5}
                      className="text-[#7ED4C6]/20 group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#7ED4C6]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#7ED4C6] text-sm uppercase tracking-[0.3em] mb-4 block">
              How We Work
            </span>
            <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mb-4">
              Our Process
            </h2>
            <div className="section-divider mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "We discuss your vision, requirements, and budget to understand your project needs." },
              { step: "02", title: "Planning", desc: "Our team develops detailed plans, timelines, and cost estimates for your approval." },
              { step: "03", title: "Construction", desc: "We execute the project with precision, keeping you informed at every stage." },
              { step: "04", title: "Completion", desc: "Final walkthrough, quality check, and handover of your completed project." },
            ].map((item, index) => (
              <div key={index} className="relative p-8 bg-[#121212] border border-[#2A2A2A]">
                <span className="font-['Oswald'] text-6xl font-bold text-[#7ED4C6]/20 absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="font-['Oswald'] text-xl font-semibold text-white mb-3 relative z-10">
                  {item.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed relative z-10">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#7ED4C6]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-[#0A0A0A]/80 text-lg mb-8">
            Book a free 30-minute consultation to discuss your project with our experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-[#0A0A0A] text-white font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#121212] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="services-schedule-btn"
            >
              <Calendar size={18} />
              Schedule a Consultation
            </Link>
            <a
              href="tel:862-350-7122"
              className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] font-['Oswald'] font-semibold uppercase tracking-wider px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone size={18} />
              Call 862-350-7122
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
